// Role table of spec §4, encoded once. The API is the enforcer; these gates only avoid dead ends.

export const STAFF_ROLES = ["superadmin", "accounts_admin", "game_admin", "finance", "support", "analytics"] as const;
export const PORTAL_ROLES = ["owner", "operator", "finance", "readonly"] as const;

export type StaffRole = (typeof STAFF_ROLES)[number];
export type PortalRole = (typeof PORTAL_ROLES)[number];
export type Role = StaffRole | PortalRole;
export type Surface = "admin" | "portal";

export const ROLE_LABELS: Record<Role, string> = {
  superadmin: "Superadmin",
  accounts_admin: "Accounts admin",
  game_admin: "Game admin",
  finance: "Finance",
  support: "Support",
  analytics: "Analytics",
  owner: "Owner",
  operator: "Operator",
  readonly: "Read only",
};

/**
 * Who may see / do what, per surface. `superadmin` (admin) and `owner` (portal) additionally hold
 * every capability of their surface implicitly.
 */
export const CAPS = {
  admin: {
    staff: ["superadmin"],
    mail: ["superadmin"],
    dashboard: STAFF_ROLES,
    partnersRead: ["accounts_admin", "support", "finance", "analytics"],
    partnersWrite: ["accounts_admin"],
    partnerUsers: ["accounts_admin"],
    brandsWrite: ["accounts_admin"],
    gamesRead: STAFF_ROLES,
    games: ["game_admin"],
    reports: ["accounts_admin", "finance", "analytics"],
    financeRead: ["finance", "accounts_admin", "analytics"],
    financeWrite: ["finance", "accounts_admin"],
    termsWrite: ["finance", "accounts_admin"],
    bonusRead: ["support", "analytics", "accounts_admin"],
    campaignsWrite: ["accounts_admin"],
    grantsCancel: ["support"],
    rounds: ["support", "analytics"],
    auditActions: ["accounts_admin"],
    auditEvents: ["support", "analytics"],
    audit: ["accounts_admin", "support", "analytics"],
    leaderboards: ["accounts_admin"],
    p2p: STAFF_ROLES,
    p2pForceCancel: ["finance"],
    p2pRetry: ["finance", "support"],
    p2pChat: ["support"],
    p2pChatPurge: ["support"],
  },
  portal: {
    dashboard: ["operator", "finance", "readonly"],
    integration: ["readonly"],
    integrationWrite: ["owner"],
    brandsRead: ["operator", "readonly"],
    brandsWrite: ["operator"],
    bonusRead: ["operator", "readonly"],
    bonusWrite: ["operator"],
    reports: ["finance", "readonly"],
    rounds: ["operator", "finance", "readonly"],
    statements: ["finance", "readonly"],
    terms: ["finance"],
    users: ["owner"],
    activity: ["owner"],
    p2p: PORTAL_ROLES,
    p2pSettingsWrite: ["operator"],
  },
} as const satisfies { admin: Record<string, readonly StaffRole[]>; portal: Record<string, readonly PortalRole[]> };

export type Actor = { user_type?: string; roles?: string[] } | null | undefined;

export const surfaceOf = (me: Actor): Surface | null =>
  me?.user_type === "BO_OPERATOR" ? "admin"
  : me?.user_type === "PORTAL" ? "portal"
  : null;

/**
 * True when the actor holds one of `roles` (or is superadmin / owner, which hold every role of their
 * surface). Surface itself is gated by the route tree, not here.
 */
export function hasAnyRole(me: Actor, roles: readonly Role[]): boolean {
  const surface = surfaceOf(me);
  if (!surface) return false;
  const held = me?.roles ?? [];
  if (held.includes(surface === "admin" ? "superadmin" : "owner")) return true;
  return roles.some((r) => held.includes(r));
}
